import { forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { craActions } from "../context/cra-slice";
import { RootState } from "src/context/store";
import FilterAccordion from "./FilterAccordion";
import { counterActions } from "src/context/counter-slice";
import { Button } from "src/components";
import { MODULE_NAME_LIST } from "src/pages/invoice-master/invoice-helper"; //M10-16170

interface FilterItem {
  id?: string;
  name?: string;
  code?: string | number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any; // Add an index signature
}

interface CounterFilter {
  onCloseClick: () => void;
  companyDetails: FilterItem[];
  divisionDetails: FilterItem[];
  cityDetails?: FilterItem[];
  linkDetails?: FilterItem[];
  stateDetails?: FilterItem[];
  approvedByMeStatus?: FilterItem[];
  pendingStatus?: FilterItem[];
  uinStatus?: FilterItem[];
  districtDetails?: FilterItem[];
  requestType?: FilterItem[]; //M10-12016
  isApprovedByMe?: boolean;
  isPending?: boolean;
  isUIN?: boolean;
}

const Divider = () => <div className="w-full h-[0.0625rem] my-2 bg-[#0000001a]" />;

const CountersFilter = forwardRef<HTMLElement, CounterFilter>(
  (
    {
      companyDetails,
      divisionDetails,
      cityDetails,
      isApprovedByMe = false,
      isUIN = false,
      linkDetails,
      stateDetails,
      districtDetails,
      approvedByMeStatus,
      pendingStatus,
      uinStatus,
      isPending,
      requestType //M10-12016
    },
    ref
  ) => {
    const { ApprovedByMeFilters, PendingFilters, UinFilters } = useSelector((state: RootState) => state.cra);
    const dispatch = useDispatch();

    const companyFilterHandler = (details: FilterItem[]) => {
      if (isUIN) dispatch(craActions.setUinFilter({ key: "company", value: details }));
      else dispatch(craActions.setCRAFilter({ isApprovedByMe, key: "company", value: details }));
    };

    const divisionFilterHandler = (details: FilterItem[]) => {
      if (isUIN) dispatch(craActions.setUinFilter({ key: "division", value: details }));
      else dispatch(craActions.setCRAFilter({ isApprovedByMe, key: "division", value: details }));
    };

    const cityFilterHandler = (details: FilterItem[]) => {
      if (isUIN) dispatch(craActions.setUinFilter({ key: "city", value: details }));
      dispatch(craActions.setCRAFilter({ isApprovedByMe, key: "city", value: details }));
    };

    const linkFilterHandler = (details: FilterItem[]) => {
      dispatch(craActions.setCRAFilter({ isApprovedByMe, key: "link", value: details }));
    };

    const statusFilterHandler = (details: FilterItem[]) => {
      dispatch(craActions.setCRAFilter({ isApprovedByMe, key: "status", value: details }));
    };

    const stateFilterHandler = (details: FilterItem[]) => {
      if (isUIN) dispatch(craActions.setUinFilter({ key: "state", value: details }));
      dispatch(craActions.setCRAFilter({ isApprovedByMe, key: "state", value: details }));
    };

    const districtFilterHandler = (details: FilterItem[]) => {
      if (isUIN) dispatch(craActions.setUinFilter({ key: "district", value: details }));
      dispatch(craActions.setCRAFilter({ isApprovedByMe, key: "district", value: details }));
    };
    //M10-12016
    const requestTypeFilterHandler = (details: FilterItem[]) => {
      dispatch(craActions.setCRAFilter({ isApprovedByMe, key: "requestType", value: details }));
    };
    //M10-12016
    //M10-16170
    const moduleFilterHandler = (details: FilterItem[]) => {
      dispatch(craActions.setCRAFilter({ isApprovedByMe, key: "module", value: details }));
    };
    //M10-16170

    const resetFilter = () => {
      if (isUIN) {
        dispatch(craActions.setUinFilter({ key: "company", value: [] }));
        dispatch(craActions.setUinFilter({ key: "division", value: [] }));
        dispatch(craActions.setUinFilter({ key: "city", value: [] }));
        dispatch(craActions.setUinFilter({ key: "state", value: [] }));
        dispatch(craActions.setUinFilter({ key: "district", value: [] }));
      } else {
        dispatch(craActions.setCRAFilter({ isApprovedByMe, key: "company", value: [] }));
        dispatch(craActions.setCRAFilter({ isApprovedByMe, key: "division", value: [] }));
        dispatch(craActions.setCRAFilter({ isApprovedByMe, key: "city", value: [] }));
        dispatch(craActions.setCRAFilter({ isApprovedByMe, key: "link", value: [] }));
        dispatch(craActions.setCRAFilter({ isApprovedByMe, key: "status", value: [] }));
        dispatch(craActions.setCRAFilter({ isApprovedByMe, key: "state", value: [] }));
        dispatch(craActions.setCRAFilter({ isApprovedByMe, key: "district", value: [] }));
        dispatch(craActions.setCRAFilter({ isApprovedByMe, key: "requestType", value: [] })); //M10-12016
        dispatch(craActions.setCRAFilter({ isApprovedByMe, key: "module", value: [] })); //M10-16170
      }
      dispatch(counterActions.setIsResetFilter(false));
    };

    const filters = isUIN ? UinFilters : isApprovedByMe ? ApprovedByMeFilters : PendingFilters;

    return (
      <aside
        className="bg-white border-l-[1px] min-w-[20rem] overflow-y-auto max-w-[25rem] border-l-[#00000014] fixed px-8 text-[#49484C] text-xl font-semibold py-5 right-0  h-screen"
        ref={ref}
      >
        <div className="flex items-center justify-between gap-5 mt-3">
          <h3 className="whitespace-nowrap">Fetch filters</h3>
          <Button className="border py-1 bg-[#7F56D9] text-white" onClick={resetFilter}>
            Reset
          </Button>
        </div>

        {/* filters  */}
        <div className="flex flex-col ">
          <FilterAccordion
            uniqueKey="code"
            displayKey="shortname"
            onSelectingFilter={companyFilterHandler}
            title="Company Name"
            arrayToFilter={companyDetails}
            defaultValue={filters.company}
          />
          <Divider />
          <FilterAccordion
            uniqueKey="division_name"
            displayKey="division_name"
            title="Division"
            arrayToFilter={divisionDetails}
            onSelectingFilter={divisionFilterHandler}
            defaultValue={filters.division}
          />

          <Divider />
          <FilterAccordion
            uniqueKey="state_code"
            displayKey="state_name"
            title="State"
            defaultValue={filters.state}
            onSelectingFilter={stateFilterHandler}
            arrayToFilter={stateDetails || []}
          />

          <Divider />
          <FilterAccordion
            uniqueKey="city_code"
            displayKey="city_name"
            title="City"
            defaultValue={filters.city}
            onSelectingFilter={cityFilterHandler}
            arrayToFilter={cityDetails || []}
          />

          <Divider />
          <FilterAccordion
            uniqueKey="district_code"
            displayKey="district_name"
            title="District"
            defaultValue={filters.district}
            onSelectingFilter={districtFilterHandler}
            arrayToFilter={districtDetails || []}
          />

          {linkDetails && linkDetails?.length > 0 && (
            <>
              <Divider />
              <FilterAccordion
                displayKey="dr_ph_lnk_code"
                title="Link Type"
                arrayToFilter={(linkDetails || [])?.filter(
                  (item: FilterItem) => item?.dr_ph_lnk_code !== "DRNT" && item?.dr_ph_lnk_code !== "INDPH-NT"
                )}
                uniqueKey="drph_lnk_type_id"
                onSelectingFilter={linkFilterHandler}
                defaultValue={filters.link}
              />
            </>
          )}

          {approvedByMeStatus && approvedByMeStatus?.length > 0 && (
            <>
              <Divider />
              <FilterAccordion
                displayKey="label"
                title="Status"
                arrayToFilter={approvedByMeStatus || []}
                uniqueKey="value"
                onSelectingFilter={statusFilterHandler}
                defaultValue={filters.status}
              />
            </>
          )}
          {pendingStatus && pendingStatus?.length > 0 && (
            <>
              <Divider />
              <FilterAccordion
                displayKey="label"
                title="Status"
                arrayToFilter={pendingStatus || []}
                uniqueKey="value"
                onSelectingFilter={statusFilterHandler}
                defaultValue={filters.status}
              />
            </>
          )}

          {uinStatus && uinStatus?.length > 0 && (
            <>
              <Divider />
              <FilterAccordion
                displayKey="label"
                title="Status"
                arrayToFilter={uinStatus || []}
                uniqueKey="value"
                onSelectingFilter={statusFilterHandler}
                defaultValue={filters.status}
              />
            </>
          )}
          {/* M10-12016 */}
          {(isPending || isApprovedByMe) && (
            <>
              <Divider />
              <FilterAccordion
                uniqueKey="creqtype_id"
                displayKey="creqtype_name"
                title="Request Type"
                defaultValue={filters.requestType}
                onSelectingFilter={requestTypeFilterHandler}
                arrayToFilter={requestType || []}
              />
            </>
          )}
          {/* M10-12016 */}
          {/* //M10-16170 */}
          {(isPending || isApprovedByMe) && (
            <>
              <Divider />
              <FilterAccordion
                uniqueKey="key"
                displayKey="value"
                title="Module"
                defaultValue={(filters as typeof PendingFilters).module}
                onSelectingFilter={moduleFilterHandler}
                arrayToFilter={MODULE_NAME_LIST}
              />
            </>
          )}
          {/* //M10-16170 */}
        </div>
      </aside>
    );
  }
);

export default CountersFilter;
